import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, MapPin, Heart, LogOut, Plus, Trash2, UserRound } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import ProductVial from "@/components/ProductVial";
import { getProductImage } from "@/constants/productImages";
import { toast } from "sonner";

const emptyAddress = { label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "United States", phone: "" };

export default function AccountDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [newAddress, setNewAddress] = useState(emptyAddress);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [profile, setProfile] = useState({ name: user?.name || "", company: user?.company || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", company: user.company || "" });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await apiClient.put("/account/profile", profile);
      await refreshUser();
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    apiClient.get("/account/orders").then((res) => setOrders(res.data)).catch(() => {});
    apiClient.get("/account/wishlist").then((res) => setWishlist(res.data)).catch(() => {});
  }, []);

  const addAddress = async (e) => {
    e.preventDefault();
    const { data } = await apiClient.post("/account/addresses", newAddress);
    setAddresses(data);
    setNewAddress(emptyAddress);
    setShowAddressForm(false);
    toast.success("Address saved");
  };

  const removeAddress = async (id) => {
    const { data } = await apiClient.delete(`/account/addresses/${id}`);
    setAddresses(data);
  };

  const removeWishlistItem = async (productId) => {
    await apiClient.delete(`/account/wishlist/${productId}`);
    setWishlist((prev) => prev.filter((p) => p._id !== productId));
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="account-dashboard-page">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-medium text-slate-900">My Account</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="account-logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </Button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList data-testid="account-tabs">
          <TabsTrigger value="orders" data-testid="account-tab-orders"><Package className="h-4 w-4 mr-1.5" /> Orders</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="account-tab-addresses"><MapPin className="h-4 w-4 mr-1.5" /> Addresses</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="account-tab-wishlist"><Heart className="h-4 w-4 mr-1.5" /> Wishlist</TabsTrigger>
          <TabsTrigger value="profile" data-testid="account-tab-profile"><UserRound className="h-4 w-4 mr-1.5" /> Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <form onSubmit={saveProfile} className="border border-slate-200 rounded-xl p-5 max-w-lg space-y-4" data-testid="account-profile-form">
            <div>
              <Label>Researcher Name</Label>
              <Input required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" />
            </div>
            <div>
              <Label>Company / Institution (optional)</Label>
              <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} placeholder="Laboratory, university, or research organization" data-testid="profile-company-input" />
              <p className="text-xs text-slate-500 mt-1.5">Pre-fills at checkout for institutional shipping labels.</p>
            </div>
            <Button type="submit" size="sm" disabled={savingProfile} className="rounded-full btn-lift" data-testid="save-profile-btn">
              {savingProfile ? "Saving..." : "Save Profile"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="account-no-orders">No orders yet.</p>
          ) : orders.map((o) => (
            <Link key={o.order_number} to={`/account/orders/${o.order_number}`} className="flex items-center justify-between border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`account-order-${o.order_number}`}>
              <div>
                <p className="font-semibold text-sm text-slate-900">{o.order_number}</p>
                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()} &middot; {o.items.length} item(s)</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-sm text-slate-900">${o.total.toFixed(2)}</p>
                <p className="text-xs capitalize text-amber-600">{o.payment_status}</p>
              </div>
            </Link>
          ))}
        </TabsContent>

        <TabsContent value="addresses" className="mt-6 space-y-3">
          {addresses.map((a) => (
            <div key={a.id} className="flex items-center justify-between border border-slate-200 rounded-xl p-4" data-testid={`account-address-${a.id}`}>
              <div className="text-sm">
                <p className="font-semibold text-slate-900">{a.label} — {a.full_name}</p>
                <p className="text-slate-500">{a.line1}, {a.city}, {a.state} {a.postal_code}, {a.country}</p>
              </div>
              <button onClick={() => removeAddress(a.id)} className="text-slate-400 hover:text-red-500" data-testid={`remove-address-${a.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}

          {showAddressForm ? (
            <form onSubmit={addAddress} className="border border-slate-200 rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="add-address-form">
              <Input placeholder="Label (e.g. Home)" value={newAddress.label} onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })} />
              <Input placeholder="Full Name" required value={newAddress.full_name} onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })} />
              <Input placeholder="Address Line 1" required className="sm:col-span-2" value={newAddress.line1} onChange={(e) => setNewAddress({ ...newAddress, line1: e.target.value })} />
              <Input placeholder="City" required value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} />
              <Input placeholder="State" required value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })} />
              <Input placeholder="Postal Code" required value={newAddress.postal_code} onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })} />
              <Input placeholder="Country" required value={newAddress.country} onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })} />
              <div className="sm:col-span-2 flex gap-2">
                <Button type="submit" size="sm" className="rounded-full btn-lift" data-testid="save-address-btn">Save Address</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddressForm(false)}>Cancel</Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowAddressForm(true)} data-testid="show-add-address-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Address</Button>
          )}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-6">
          {wishlist.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="account-no-wishlist">Your wishlist is empty.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlist.map((p) => (
                <div key={p._id} className="border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`wishlist-item-${p._id}`}>
                  {getProductImage(p.slug) ? (
                    <img src={getProductImage(p.slug)} alt={p.name} className="h-16 w-16 object-contain" />
                  ) : (
                    <ProductVial categorySlug={p.category_id} name={p.name} strength={p.strength} className="h-16 w-16" />
                  )}
                  <div className="flex-1">
                    <Link to={`/product/${p.slug}`} className="font-semibold text-sm text-slate-900">{p.name}</Link>
                    <p className="text-xs text-slate-500">${p.price.toFixed(2)}</p>
                  </div>
                  <button onClick={() => removeWishlistItem(p._id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
